import type { MuiSx } from '@/shared/types';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { Link } from '../components/Link';
import { externalLinks, footerMenu } from '../constants/Constants';
import { theme } from '@theguarantors/tg-package-material-ui';
import { useLocation } from 'react-router';

const MOBILE_FORM_NAVIGATION_HEIGHT = '164px';

type SiteFooterProps = {
  isSplitView?: boolean;
};

const CopyrightText = ({ lineHeight }: { lineHeight: string }) => {
  const currentYear = new Date().getFullYear();

  return (
    <Typography
      color={theme.palette.neutralGrey[500]}
      variant="helpertext"
      sx={{
        height: lineHeight,
        lineHeight: lineHeight,
      }}
    >
      © {currentYear} TheGuarantors Inc.
    </Typography>
  );
};

const splitViewStyles = {
  pt: { xs: 0, sm: 0, lg: 0 },
  mt: 0,
  pb: '20px',
} satisfies MuiSx;

const defaultStyles = {
  pt: { xs: 15, sm: 15, lg: 5 },
  mt: '144px',
} satisfies MuiSx;

const SiteFooter = ({ isSplitView = false }: SiteFooterProps) => {
  const sx = isSplitView ? splitViewStyles : defaultStyles;
  const { pathname } = useLocation();

  const isFormNavigationPage =
    pathname.includes('/claims/form') || pathname.includes('/users/create');
  const paddingBottom = isFormNavigationPage ? { xs: MOBILE_FORM_NAVIGATION_HEIGHT, sm: 0 } : 0;

  const lineHeight = '16px';

  return (
    <Box sx={{ width: '100%', paddingBottom }}>
      <Stack
        direction={{ xs: 'column' }}
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        sx={sx}
        gap="8px"
        py="16px !important"
        mx={{ xs: '12px', lg: 4 }}
      >
        <Typography
          variant="helpertext"
          textAlign={'left'}
          sx={{
            opacity: '.7',
            lineHeight: lineHeight,
          }}
        >
          TheGuarantors is a licensed insurance agent and broker. For detailed information, please
          see our{' '}
          <Link
            underline="always"
            data-variant="noColor"
            target="_blank"
            color={theme.palette.neutralGrey[500]}
            href={externalLinks.legalRegulatory}
            sx={{
              textDecorationColor: 'grey',
              lineHeight: lineHeight,
            }}
          >
            Legal/Regulatory Notice
          </Link>
          .
        </Typography>
        <Grid
          container
          direction="row"
          justifyContent={'flex-start'}
          sx={{ marginTop: '0px !important' }}
        >
          <CopyrightText lineHeight={lineHeight} />
          {footerMenu.map((item, index) => [
            <Typography
              key={`divider-${index}`}
              color={theme.palette.neutralGrey[500]}
              variant="helpertext"
              sx={{
                opacity: 0.3,
                height: lineHeight,
                lineHeight: lineHeight,
                mx: 0.5,
              }}
            >
              |
            </Typography>,
            <Link
              key={index}
              variant="helpertext"
              color={theme.palette.neutralGrey[500]}
              target="_blank"
              href={item.to}
              underline="hover"
              role="footerLinks"
              sx={{
                textAlign: 'center',
                height: lineHeight,
                lineHeight: lineHeight,
              }}
            >
              {item.title}
            </Link>,
          ])}
        </Grid>
      </Stack>
    </Box>
  );
};

export default SiteFooter;
